import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Quote } from 'lucide-react';
import type { ArticleBlock as ArticleBlockType } from '../../types/advertorial';
interface ArticleBlockProps {
  block: ArticleBlockType;
}
const reveal = {
  initial: {
    opacity: 0,
    y: 22
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.6,
    ease: 'easeOut' as const
  }
};
export function ArticleBlock({
  block
}: ArticleBlockProps) {
  switch (block.type) {
    case 'p':
      return <motion.p {...reveal} className={['text-[15.5px] font-light leading-[1.75] text-smoke md:text-[17.5px] md:leading-[1.78]', block.lead ? 'text-ivory/90' : '', block.dropCap ? 'drop-cap' : ''].filter(Boolean).join(' ')} data-id="element-12" data-mp="16uazi4hnyw36">
          {block.text}
        </motion.p>;
    case 'quote':
      return <motion.blockquote {...reveal} className="relative my-2 border-l-2 border-gold bg-gold/[0.06] py-5 pl-5 pr-4 md:py-6 md:pl-8 md:pr-5" data-id="element-13" data-mp="1pnoozu2lyabe">
          <Quote aria-hidden="true" className="absolute -left-[13px] top-4 h-6 w-6 rounded-full bg-carbon p-1 text-gold md:top-5" data-id="element-14" data-mp="yfuu65hutk7e" />
          <p className="font-serif text-[18px] italic leading-[1.5] text-[#EFE7D6] md:text-[24px]" data-id="element-15" data-mp="17epwv7lowc5y">
            {block.text}
          </p>
        </motion.blockquote>;
    case 'banner':
      return <motion.p {...reveal} className="border-y border-gold/25 py-4 text-center font-serif text-[17.5px] font-semibold leading-snug text-gold-soft md:py-5 md:text-[22px]" data-id="element-16" data-mp="1h5ubg67gdv09">
          {block.text}
        </motion.p>;
    case 'figure':
      return <motion.figure {...reveal} className="my-3 md:my-4 lg:-mx-20" data-id="element-17" data-mp="q9l9htbt8ky">
          <div className="overflow-hidden rounded-sm border border-white/10" data-id="element-18" data-mp="sm8oahusb5e">
            <img src={block.src} alt={block.alt} className="h-full w-full object-cover" loading="lazy" decoding="async" data-id="element-19" data-mp="3swev9mvt05lj" />
          </div>
          <figcaption className="mt-2.5 flex gap-2.5 font-mono text-[10px] leading-relaxed tracking-[0.03em] text-ash/80 md:mt-3 md:gap-3 md:text-[10.5px]" data-id="element-20" data-mp="20uvjg0cm4hpe">
            <span aria-hidden="true" className="mt-[6px] h-px w-5 shrink-0 bg-gold/60 md:mt-[7px] md:w-6" data-id="element-21" data-mp="2arzylg81jcwp" />
            {block.caption}
          </figcaption>
        </motion.figure>;
    case 'stat':
      return <motion.aside {...reveal} className="my-2 flex flex-col gap-3.5 rounded-sm border border-white/10 bg-panel px-5 py-5 sm:flex-row sm:items-center sm:gap-7 sm:px-6 sm:py-6" data-id="element-22" data-mp="2jpttcmq5pyhr">
          <p className="font-serif text-[40px] font-bold leading-none text-gold-soft md:text-[56px]" data-id="element-23" data-mp="2tjkf3juwfutr">
            {block.value}
          </p>
          <div className="sm:border-l sm:border-white/10 sm:pl-7" data-id="element-24" data-mp="32hmoamosdts4">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold" data-id="element-25" data-mp="qv2m0n6btfc8">{block.label}</p>
            <p className="mt-2 text-[14.5px] font-light leading-relaxed text-ash" data-id="element-26" data-mp="mth3omb0w8un">{block.detail}</p>
          </div>
        </motion.aside>;
    case 'verse':
      return <motion.blockquote {...reveal} className="my-2 rounded-sm border border-gold/20 bg-gold/[0.05] px-5 py-6 text-center sm:px-6 sm:py-7" data-id="element-27" data-mp="hwgy0ci35842">
          <p className="font-serif text-[19px] italic leading-[1.55] text-parchment md:text-[25px]" data-id="element-28" data-mp="1aiifmjj2e24o">
            {block.text}
          </p>
          <cite className="mt-4 block font-mono text-[10px] not-italic uppercase tracking-[0.22em] text-gold" data-id="element-29" data-mp="10t8op6zyipi5">
            {block.reference}
          </cite>
        </motion.blockquote>;
    case 'cycle':
      return <motion.ol {...reveal} className="my-2 grid gap-3 sm:grid-cols-2" data-id="element-30" data-mp="1t9t46u1bi2te">
          {block.steps.map((step, i) => <li key={step.label} className="relative rounded-sm border border-white/10 bg-panel px-5 py-5" data-id="element-31" data-mp="1oi1awbqs1izk" data-mpi={i}>
              <div className="flex items-center gap-2" data-id="element-32" data-mp="1d2etnvpx6597">
                <span className="font-mono text-[10px] tracking-[0.18em] text-gold" data-id="element-33" data-mp="1d3ybhqr84od3">
                  0{i + 1}
                </span>
                <span className="font-serif text-[17px] font-semibold text-ivory" data-id="element-34" data-mp="z4ynmlbjcdhz">{step.label}</span>
                <ArrowRight aria-hidden="true" className="ml-auto h-3.5 w-3.5 text-white/25" data-id="element-35" data-mp="143jl4czekj2q" />
              </div>
              <p className="mt-2 text-[14px] font-light leading-relaxed text-ash" data-id="element-36" data-mp="1wq4fznf37db1">{step.text}</p>
            </li>)}
        </motion.ol>;
    default:
      return null;
  }
}